import React, { createRef, Component } from 'react';
import PropTypes from 'prop-types';
import classname from 'classnames';

import formHelpers from '../../../shared/forms/formHelpers';
import {
  ARROW_LEFT,
  ARROW_RIGHT,
  ARROW_DOWN,
  ARROW_UP,
  ENTER,
  ESCAPE,
} from '../../../shared/keysConstants';

import CellInput from './CellInput.component';
import { isValidNumberChar, hasRequiredFields, measureElement } from './helpers';
import formInitialState from './formInitialState';
import PopupForm from './PopupForm.component';
import { FormOptionsPropTypes } from './propTypes';
import { AMOUNT_HAS_CHANGED_FIELD_NAME } from './constants';

import {
  hasToValidateReclassAccountIdsInPopupForm,
  validateReclassAccountId,
} from '../../shared/taxEntry.validators';

export default class CellEditor extends Component {
  static propTypes = {
    api: PropTypes.shape({
      getValue: PropTypes.func,
      setValue: PropTypes.func,
      stopEditing: PropTypes.func,
      getFocusedCell: PropTypes.func,
      setFocusedCell: PropTypes.func,
      getDisplayedColBefore: PropTypes.func,
      getDisplayedColAfter: PropTypes.func,
      gridPanel: PropTypes.shape({
        eCenterViewport: PropTypes.shape({
          offsetTop: PropTypes.number,
          clientHeight: PropTypes.number,
        }),
      }),
    }),
    gridApi: PropTypes.shape({
      getAllDisplayedColumns: PropTypes.func,
    }),
    column: PropTypes.shape({
      colId: PropTypes.string.isRequired,
      left: PropTypes.bool,
      actualWidth: PropTypes.bool,
    }),
    agGridReact: PropTypes.shape({
      props: PropTypes.shape({
        rowData: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
      }).isRequired,
    }).isRequired,
    value: PropTypes.shape({
      data: PropTypes.shape({
        value: PropTypes.number,
      }),
    }).isRequired,
    formOptions: FormOptionsPropTypes,
    charPress: PropTypes.string,
    node: PropTypes.shape({
      rowTop: PropTypes.number.isRequired,
      rowHeight: PropTypes.number.isRequired,
    }),
    onValueChange: PropTypes.func,
  };

  static defaultProps = {
    formOptions: {},
  };

  constructor(props) {
    super(props);

    const { formOptions, value } = props;

    const formHasRequiredFields = hasRequiredFields(formOptions);
    const initialFormState = formInitialState(formOptions);
    this.state = {
      amount: value.data.value || '',
      formHasRequiredFields,
      formState: {
        ...initialFormState,
        globalElementsToValidate: ['areReclassAccountIdsUnique'],
        areReclassAccountIdsUnique: true,
      },
      popupOpen: formHasRequiredFields,
      popupDetailsOpen: !formHasRequiredFields,
      stopEditing: false,
      isCellInputDirty: false,
      popupSize: { width: 0, height: 0 },
    };

    this.inputRef = createRef();
    this.popupRef = createRef();
    this.prevFormDataRef = initialFormState.formData;
  }

  componentDidMount() {
    const { charPress } = this.props;
    // fixes "input not being focused when navigating between editable cells" issue
    setTimeout(() => {
      this.focusInput();
      if (charPress) {
        this.updateValueSilently(charPress);
      }
    }, 15);

    this.setMeasurements();
    document.addEventListener('keydown', this.onKeyDown);
  }

  componentDidUpdate(prevProps, prevState) {
    const { formState } = this.state;
    const { formData } = formState;
    const { formState: prevFormState } = prevState;

    if (hasToValidateReclassAccountIdsInPopupForm(prevFormState.formData, formData)) {
      validateReclassAccountId({
        formData,
        areReclassAccountIdsUnique: formState.areReclassAccountIdsUnique,
        setState: this.updateFormState.bind(this),
        callback: this.validateAll.bind(this),
        inPopupForm: true,
      });
    }
    this.prevFormDataRef = formData;
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
  }

  getValue() {
    const { amount } = this.state;
    const { value } = this.props;

    if (!this.amountHasBeenChanged()) {
      return value;
    }

    if (!this.isFormCurrentlyValid()) {
      return {
        ...value,
        validationFailed: true,
      };
    }

    const finalAmount = amount === '' ? null : parseFloat(amount) || 0;
    const originalValue = parseFloat(value?.data?.value || '') || 0;
    const taxEntryAmount = (finalAmount || 0) - originalValue;
    const formValues = this.getFormValues();

    const cleanValue = { ...value };
    delete cleanValue.validationFailed;
    return {
      ...cleanValue,
      data: {
        ...cleanValue.data,
        ...formValues,
        value: finalAmount,
        amount: taxEntryAmount,
      },
    };
  }

  isCancelAfterEnd() {
    const hasChanges = this.amountHasBeenChanged();

    const shouldCancel = !hasChanges || !this.isFormCurrentlyValid();

    return shouldCancel;
  }

  afterGuiAttached() {
    this.focusInput();
  }

  isPopup() {
    return false;
  }

  isCancelBeforeStart() {
    return false;
  }

  focusIn() {
    this.focusInput();
  }

  focusOut() {
    // Empty method - required by AG Grid interface
  }

  isFormCurrentlyValid = () => {
    const { formState } = this.state;
    return formState.isFormValid;
  };

  amountHasBeenChanged = () => {
    const { amount } = this.state;
    const { value } = this.props;
    return String(amount || '') !== String(value.data.value || '');
  };

  getFormValues = () => {
    const { formState } = this.state;
    const { formData } = formState;
    return Object.entries(formData).reduce((acc, next) => {
      const [key, val] = next;

      if (
        key === AMOUNT_HAS_CHANGED_FIELD_NAME
        || !val
        || (!val.value && typeof val.value !== 'boolean')
      ) {
        return acc;
      }

      return {
        ...acc,
        [key]: val.value,
      };
    }, {
      files: formData.files,
    });
  };

  updateFormState = updater => {
    if (typeof updater === 'function') {
      this.setState( prevState => {
        const helperState = {
          formData: prevState.formState.formData,
          isFormValid: prevState.formState.isFormValid,
          triedToSubmitForm: prevState.formState.triedToSubmitForm,
          elementsToValidate: prevState.formState.elementsToValidate,
          globalElementsToValidate: prevState.formState.globalElementsToValidate,
          areReclassAccountIdsUnique: prevState.formState.areReclassAccountIdsUnique,
        };
        const updatedHelperState = updater(helperState);
        return {
          formState: {
            ...prevState.formState,
            ...updatedHelperState,
          },
        };
      });
    } else {
      this.setState( prevState => ({
        formState: { ...prevState.formState, ...updater },
      }));
    }
  };

  validateAll = () => {
    formHelpers.validateAll(this.updateFormState, { filesRequired: false });
  };

  setAmountHasChanged = () => {
    const hasChanged = this.amountHasBeenChanged();

    this.setState( state => ({
      formState: {
        ...state.formState,
        formData: {
          ...state.formState.formData,
          amountHasChanged: {
            value: hasChanged,
            isValid: hasChanged,
          },
        },
      },
    }), this.validateAll);
  };

  updateValueSilently = newValue => {
    if (isValidNumberChar(newValue) || newValue === '-') {
      this.setState({ amount: newValue, isCellInputDirty: true }, () => {
        this.setAmountHasChanged();
      });
    } else if (!this.state.isCellInputDirty) {
      setTimeout(() => {
        if (this.inputRef.current) {
          this.inputRef.current.select();
        }
      }, 1);
    }
  };

  updateValue = newValue => {
    if (isValidNumberChar(newValue) || newValue === '-') {
      this.setState({ amount: newValue, isCellInputDirty: true }, () => {
        this.setAmountHasChanged();

        if (this.amountHasBeenChanged()) {
          this.notifyGridOfValueChange();
        }
      });
    } else if (!this.state.isCellInputDirty) {
      setTimeout(() => {
        if (this.inputRef.current) {
          this.inputRef.current.select();
        }
      }, 1);
    }
  };

  focusInput = () => {
    if (this.inputRef.current) {
      this.inputRef.current.focus();
    }
  };

  notifyGridOfValueChange = () => {
    const { onValueChange } = this.props;
    if (onValueChange) {
      if (!this.amountHasBeenChanged()) {
        return;
      }

      const newValue = this.getValue();
      onValueChange(newValue);
    }
  };

  setMeasurements = () => {
    // Effectively has to render the element twice because
    // it can't be measured before it is rendered.
    setTimeout(() => {
      if (this.popupRef.current) {
        this.setState({
          popupSize: measureElement(this.popupRef.current),
        });
      }
    }, 0);
  };

  handleChange = event => {
    const { value } = event.target;
    this.updateValue(value);
  };

  onKeyDown = event => {
    const { api, agGridReact } = this.props;

    if (event.key === ENTER || event.code === ENTER) {
      this.onSubmit({ onKeyDownSubmit: true });
    }
    if (event.key === ESCAPE || event.code === ESCAPE) {
      this.togglePopup();
      api.stopEditing();
    }
    if (event.key === ARROW_UP || event.code === ARROW_UP) {
      const { rowIndex, column, floating } = api.getFocusedCell();
      if (rowIndex > 0) {
        api.setFocusedCell(rowIndex - 1, column.colId, floating);
      }
    }
    if (event.key === ARROW_DOWN || event.code === ARROW_DOWN) {
      const { rowIndex, column, floating } = api.getFocusedCell();
      if (!agGridReact || (agGridReact.props.rowData.length > rowIndex + 1)) {
        api.setFocusedCell(rowIndex + 1, column.colId, floating);
      }
    }
    if (event.key === ARROW_LEFT || event.code === ARROW_LEFT) {
      const { rowIndex, column, floating } = api.getFocusedCell();
      const columnBefore = api.getDisplayedColBefore(column);
      if (columnBefore) {
        api.setFocusedCell(rowIndex, columnBefore.colId, floating);
      }
    }
    if (event.key === ARROW_RIGHT || event.code === ARROW_RIGHT) {
      const { rowIndex, column, floating } = api.getFocusedCell();
      const columnAfter = api.getDisplayedColAfter(column);
      if (columnAfter) {
        api.setFocusedCell(rowIndex, columnAfter.colId, floating);
      }
    }
  };

  changeProperty = event => {
    formHelpers.changeProperty({
      event,
      setState: this.updateFormState,
    });
  };

  selectProperty = (key, value) => {
    formHelpers.selectProperty(key, 'value', value, this.updateFormState, '', this.validateAll);
  };

  addFile = file => {
    formHelpers.addFileToForm({
      file,
      setState: this.updateFormState,
      callback: this.validateAll,
    });
  };

  removeFile = fileToRemove => {
    formHelpers.removeFileFromForm({
      fileToRemove,
      setState: this.updateFormState,
      callback: this.validateAll,
    });
  };

  validate = (key, { isValid }) => {
    formHelpers.validate({
      key,
      isValid,
      setState: this.updateFormState,
      callback: this.validateAll,
    });
  };

  validationSubscription = (key, operation) => {
    formHelpers.validationSubscription({
      key,
      operation,
      setState: this.updateFormState,
      callback: this.validateAll,
    });
  };

  togglePopup = () => {
    this.setState( state => ({
      popupOpen: !state.popupOpen,
    }));
  };

  togglePopupDetails = () => {
    this.setState( state => ({
      popupDetailsOpen: !state.popupDetailsOpen,
    }), this.setMeasurements);
  };


  stopEditing = () => {
    const { api } = this.props;

    this.setState({ stopEditing: true }, () => {
      api.stopEditing();
    });
  };

  onSubmit = (options = {}) => {
    const { onKeyDownSubmit = false } = options;

    if (onKeyDownSubmit && !this.amountHasBeenChanged()) {
      return this.stopEditing();
    }

    const hasChanges = this.amountHasBeenChanged();
    const { popupOpen } = this.state;

    if (hasChanges || popupOpen) {
      this.validateAll();

      setTimeout(() => {
        const isFormValid = this.isFormCurrentlyValid();

        if (!isFormValid) {
          this.setState( state => ({
            popupOpen: true,
            formState: {
              ...state.formState,
              triedToSubmitForm: true,
            },
          }));
          return;
        }

        if (hasChanges) {
          this.notifyGridOfValueChange();
        }
        this.stopEditing();
      }, 50);
      return;
    }

    return this.stopEditing();
  };

  selectInput = () => {
    if (this.inputRef.current) {
      this.inputRef.current.select();
    }
  };

  shouldRenderOnTheLeft = () => {
    const { gridApi, column } = this.props;
    const { popupSize } = this.state;

    if (!gridApi) {
      return false;
    }

    const allColumns = gridApi.getAllDisplayedColumns();
    const lastColumn = allColumns[allColumns.length - 1];
    const lastColumnEdge = lastColumn.left + lastColumn.actualWidth;

    if (lastColumnEdge < popupSize.width) {
      return false;
    }

    return (column.left + column.actualWidth + popupSize.width) > lastColumnEdge;
  };

  topPosition = () => {
    const { api, node } = this.props;
    const { popupSize } = this.state;

    if (!api.gridPanel) {
      return 0;
    }

    const { eCenterViewport } = api.gridPanel;

    if (!eCenterViewport) {
      return 0;
    }

    const tableBodyBottom = eCenterViewport.offsetTop + eCenterViewport.clientHeight;
    const tableBodyTop = eCenterViewport.offsetTop;

    const topPosition = tableBodyBottom - tableBodyTop - node.rowTop - popupSize.height;

    return topPosition < 0 ? `${topPosition}px` : 0;
  };

  render() {
    const { formOptions } = this.props;
    const {
      amount,
      formHasRequiredFields,
      formState,
      popupOpen,
      popupDetailsOpen,
    } = this.state;

    const { formData, areReclassAccountIdsUnique, triedToSubmitForm } = formState;
    const renderOnTheLeft = this.shouldRenderOnTheLeft();

    return (
      <div className="grid-report-cell-editor">
        <CellInput
          setRef={this.inputRef}
          value={amount}
          onChange={this.handleChange}
          onFocus={this.selectInput}
        />

        <div
          className={classname('grid-report-cell-editor-more', { left: renderOnTheLeft })}
          style={{ top: this.topPosition() }}
          ref={this.popupRef}
        >
          { !popupOpen && (
            <div className="grid-report-cell-editor-moreButton" onClick={this.togglePopup}>
              More
            </div>
          )}
          { popupOpen && (
            <PopupForm
              formData={formData}
              formOptions={formOptions}
              triedToSubmitForm={triedToSubmitForm}
              addFile={this.addFile}
              removeFile={this.removeFile}
              changeProperty={this.changeProperty}
              selectProperty={this.selectProperty}
              validate={this.validate}
              validationSubscription={this.validationSubscription}
              open={popupOpen}
              showDetails={popupDetailsOpen}
              toggleDetails={this.togglePopupDetails}
              onSubmit={this.onSubmit}
              formHasRequiredFields={formHasRequiredFields}
              areReclassAccountIdsUnique={areReclassAccountIdsUnique}
            />
          )}
        </div>
      </div>
    );
  }
}
